// React hooks used to store selected state and memoize computed queue groups.
import { useEffect, useMemo, useRef, useState } from "react";



// Turn any date value into a stable "YYYY-MM-DD" key so items can be grouped by day.
function getQueueDayKey(dateValue) {

  // Convert incoming value to a Date object.
  const date = new Date(dateValue);

  // If date is invalid, return empty key so caller can skip the item.
  if (Number.isNaN(date.getTime())) return "";

  // Build zero-padded parts so the key always has the same format.
  const year = date.getFullYear();

  const month = String(date.getMonth() + 1).padStart(2, "0");

  const day = String(date.getDate()).padStart(2, "0");

  // Example output: "2026-03-03".
  return `${year}-${month}-${day}`;

}



// Turn a day key into readable UI text for the slider and section title.
function formatQueueDayLabel(dayKey) {

  // No key means no label.
  if (!dayKey) return "";

  // Parse the key at midnight so date formatting is consistent.
  const date = new Date(`${dayKey}T00:00:00`);

  // If parsing fails, fall back to showing the raw key.
  if (Number.isNaN(date.getTime())) return dayKey;

  // Format label like "Mon, Jan 1, 2024".
  return date.toLocaleDateString("en-UK", {

    weekday: "short",
    month: "short",
    day: "numeric",
    year: "numeric",

  });

}



const useQueueDayGroups = (nonInProgressVisibleImages) => {

  // Track which day the user has selected in the completed queue section.
  const [selectedQueueDayKey, setSelectedQueueDayKey] = useState("");

  // Remember the completed days which existed on the previous queue refresh.
  const previousQueueDayKeysRef = useRef(null);

  // Build queue groups from visible images and keep newest days first.
  const queueDayGroups = useMemo(() => {

    // Start with an object map: { "YYYY-MM-DD": [image, image, ...] }.
    const groupedByDay = nonInProgressVisibleImages.reduce((groups, img) => {

      // Use created date first, then started date as fallback.
      const createdValue = img?.created || img?.started;

      // Convert the date value into a day key.
      const dayKey = getQueueDayKey(createdValue);

      // Skip this image if the day key is invalid.
      if (!dayKey) return groups;

      // Create an array for this day if this is the first image for that day.
      if (!groups[dayKey]) {

        groups[dayKey] = [];

      }

      // Add the image into that day bucket.
      groups[dayKey].push(img);

      // Return the updated groups object for the next reduce iteration.
      return groups;

    }, {});

    // Convert the map into an array with UI metadata for each day.
    return Object.entries(groupedByDay).map(([dayKey, groupedImages]) => ({

        // Keep original day key so selection lookup is fast and stable.
        dayKey,

        // Build the label shown in the day slider.
        label: formatQueueDayLabel(dayKey),

        // Keep a timestamp so days can be sorted newest-first.
        dayTimestamp: new Date(`${dayKey}T00:00:00`).getTime(),

        // Sort images in each day so newest appears first.
        images: groupedImages
          .slice()
          .sort((a, b) => new Date(b?.created || b?.started || 0).getTime() - new Date(a?.created || a?.started || 0).getTime()),

      }))

      // Sort day groups so newest day appears first in the UI.
      .sort((a, b) => b.dayTimestamp - a.dayTimestamp);

  }, [nonInProgressVisibleImages]);



  // Keep the selected completed day valid and reveal a genuinely new newest day.
  useEffect(() => {

    // Build the current set of completed queue days.
    const currentQueueDayKeys = new Set(
      queueDayGroups.map((group) => group.dayKey)
    );

    // Check if queueDayGroups is empty - if it is we want to clear the selected day.
    if (queueDayGroups.length === 0) {

      // Remember that there are currently no completed days.
      previousQueueDayKeysRef.current = currentQueueDayKeys;

      // If a day is selected, clear it because there is nothing to show.
      if (selectedQueueDayKey !== "") {

        // Clear the selected day.
        setSelectedQueueDayKey("");

      }

      // Nothing else to do when queue is empty.
      return;

    }

    // Get the newest completed day.
    const newestQueueDayKey = queueDayGroups[0].dayKey;

    // Check whether this newest day did not exist on the previous queue refresh.
    const newestDayIsNew =
      previousQueueDayKeysRef.current !== null &&
      !previousQueueDayKeysRef.current.has(newestQueueDayKey);

    // Remember the current completed days ready for the next queue refresh.
    previousQueueDayKeysRef.current = currentQueueDayKeys;

    // If a genuinely new newest day has appeared, show it.
    if (newestDayIsNew) {

      setSelectedQueueDayKey(newestQueueDayKey);

      return;

    }

    // Check whether the current selected day still exists in the groups list.
    const hasSelectedDay = queueDayGroups.some((group) => group.dayKey === selectedQueueDayKey);

    // If the selected day is missing, fallback to the newest day.
    if (!hasSelectedDay) {

      setSelectedQueueDayKey(newestQueueDayKey);

    }

  }, [queueDayGroups, selectedQueueDayKey]);



  // Resolve the currently selected day group object for rendering.
  const selectedQueueDayGroup = useMemo(() => {

    // No groups means there is no selected group to return.
    if (queueDayGroups.length === 0) return null;

    // Try to find the exact group that matches the selected key.
    const matchedGroup = queueDayGroups.find((group) => group.dayKey === selectedQueueDayKey);

    // If match is missing, safely fallback to the newest day.
    return matchedGroup || queueDayGroups[0];

  }, [queueDayGroups, selectedQueueDayKey]);

  // Expose grouped day data and selection controls to the caller.
  return {

    queueDayGroups,
    selectedQueueDayKey,
    setSelectedQueueDayKey,
    selectedQueueDayGroup,

  };

};

export default useQueueDayGroups;
